<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { CONSOLIDATED_METRICS, type IConsolidatedReport, type IManagementArea } from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'

const props = defineProps<{ isOpen: boolean }>()
const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'add', report: IConsolidatedReport): void
}>()

const store = useManagementStore()

const MONTH_OPTIONS = [3, 6, 12]
const areaId = ref<number | null>(null)
const sensorId = ref<number | null>(null)
const monthsBack = ref<number>(12)
const metrics = ref<string[]>(['consumo_total', 'custo_total'])

const areaOptions = computed(() => {
  const options: { id: number; label: string }[] = []
  const walk = (areas: IManagementArea[], depth: number) => {
    areas.forEach((area) => {
      options.push({ id: area.id, label: `${'— '.repeat(depth)}${area.name}` })
      walk(area.children || [], depth + 1)
    })
  }
  walk(store.roots, 0)
  return options
})

const sensorOptions = computed(() => {
  const area = areaId.value ? store.nodesById[areaId.value] : null
  if (!area) return []
  const out: { id: number; name: string }[] = []
  const walk = (node: IManagementArea) => {
    ;(node.management_area_sensors || []).forEach((s) => {
      if (String(s.sensor_type) === 'electricity') out.push({ id: s.sensor_id, name: s.sensor_name })
    })
    ;(node.children || []).forEach(walk)
  }
  walk(area)
  return out
})

// Métricas agrupadas para os checkboxes.
const groups = computed(() => {
  const byGroup: Record<string, typeof CONSOLIDATED_METRICS> = {}
  CONSOLIDATED_METRICS.forEach((m) => {
    byGroup[m.group] = byGroup[m.group] || []
    byGroup[m.group].push(m)
  })
  return Object.entries(byGroup).map(([group, items]) => ({ group, items }))
})

const canAdd = computed(() => areaId.value !== null && metrics.value.length > 0)

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    areaId.value = store.favoriteAreaId ?? store.roots[0]?.id ?? null
    sensorId.value = null
    monthsBack.value = 12
    metrics.value = ['consumo_total', 'custo_total']
  },
)

watch(areaId, () => {
  if (sensorId.value && !sensorOptions.value.some((s) => s.id === sensorId.value)) sensorId.value = null
})

function toggleMetric(key: string) {
  const set = new Set(metrics.value)
  set.has(key) ? set.delete(key) : set.add(key)
  metrics.value = Array.from(set)
}

function submit() {
  if (!canAdd.value || areaId.value === null) return
  const areaLabel = areaOptions.value.find((a) => a.id === areaId.value)?.label.replace(/—\s/g, '') ?? 'Área'
  const sensorLabel = sensorId.value ? sensorOptions.value.find((s) => s.id === sensorId.value)?.name ?? null : null
  emit('add', {
    id: `${areaId.value}-${sensorId.value ?? 'all'}-${Date.now()}`,
    areaId: areaId.value,
    areaLabel,
    sensorId: sensorId.value,
    sensorLabel,
    monthsBack: monthsBack.value,
    metrics: [...metrics.value],
  })
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/40" @click="emit('cancel')" />
    <div class="relative flex max-h-[90vh] w-full max-w-2xl flex-col bg-white p-6 shadow-xl">
      <h3 class="text-lg font-semibold text-slate-900">Nova consulta consolidada</h3>

      <div class="mt-4 space-y-4 overflow-y-auto">
        <div class="flex flex-wrap items-center gap-3">
          <label class="text-sm font-medium text-slate-600">Área:</label>
          <select v-model.number="areaId" class="border border-zinc-300 px-3 py-2 text-sm focus:border-primary focus:outline-none">
            <option v-for="opt in areaOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
          </select>
          <label class="text-sm font-medium text-slate-600">Sensor:</label>
          <select v-model="sensorId" class="border border-zinc-300 px-3 py-2 text-sm focus:border-primary focus:outline-none">
            <option :value="null">Área toda</option>
            <option v-for="opt in sensorOptions" :key="opt.id" :value="opt.id">{{ opt.name }}</option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-slate-600">Período:</span>
          <div class="flex border border-stroke">
            <button
              v-for="opt in MONTH_OPTIONS"
              :key="opt"
              type="button"
              class="px-3 py-1.5 text-sm font-medium"
              :class="monthsBack === opt ? 'bg-primary text-white' : 'text-slate-600'"
              @click="monthsBack = opt">
              {{ opt }} meses
            </button>
          </div>
        </div>

        <div>
          <p class="mb-2 text-sm font-medium text-slate-700">Colunas (informações computadas pela API)</p>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div v-for="g in groups" :key="g.group">
              <p class="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{{ g.group }}</p>
              <label
                v-for="m in g.items"
                :key="m.key"
                class="mb-1 flex cursor-pointer items-center gap-2 border border-stroke px-3 py-1.5 text-sm transition hover:bg-slate-50"
                :class="{ 'border-primary bg-primary/5': metrics.includes(m.key) }">
                <input type="checkbox" class="h-4 w-4 accent-primary" :checked="metrics.includes(m.key)" @change="toggleMetric(m.key)" />
                <span class="text-slate-700">{{ m.label }}</span>
                <span v-if="m.unit" class="ml-auto text-xs text-slate-400">{{ m.unit }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 flex justify-end gap-3 border-t border-stroke pt-4">
        <button type="button" class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" @click="emit('cancel')">
          Cancelar
        </button>
        <button
          type="button"
          :disabled="!canAdd"
          class="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          @click="submit">
          Adicionar consulta
        </button>
      </div>
    </div>
  </div>
</template>
